/**
 * Standalone Whiteboard Demo Page (No Auth Required)
 * For testing and demonstration purposes
 */
import { useState, useCallback, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { 
  ArrowLeft, 
  Grid3X3,
  ZoomIn,
  ZoomOut,
  Undo,
  Redo,
  Maximize2,
  Minimize2,
  Check,
  Magnet,
  Download
} from 'lucide-react';
import { WhiteboardCanvas } from '../components/whiteboard/WhiteboardCanvas';
import { WhiteboardToolbar } from '../components/whiteboard/WhiteboardToolbar';
import { WhiteboardUserStory } from '../components/whiteboard/WhiteboardUserStory';
import type { 
  WhiteboardElement, 
  UserStory, 
  AcceptanceCriterion,
} from '../types/whiteboard';

export function WhiteboardDemoPage() {
  // ============================================================================
  // State
  // ============================================================================
  const [elements, setElements] = useState<WhiteboardElement[]>([]);
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [userStory, setUserStory] = useState<UserStory>({ asA: '', iWant: '', soThat: '' });
  const [acceptanceCriteria, setAcceptanceCriteria] = useState<AcceptanceCriterion[]>([]);
  
  // Canvas state
  const [zoom, setZoom] = useState(1);
  const [pan, setPan] = useState({ x: 100, y: 100 });
  const [showGrid, setShowGrid] = useState(true);
  const [gridSize] = useState(20);
  const [snapToGrid, setSnapToGrid] = useState(true);
  const [activeTool, setActiveTool] = useState<string>('select');
  
  // History for undo/redo
  const [history, setHistory] = useState<WhiteboardElement[][]>([[]]);
  const [historyIndex, setHistoryIndex] = useState(0);
  
  // UI state
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [saved, setSaved] = useState(false);
  
  // ============================================================================
  // History Management
  // ============================================================================
  const pushHistory = useCallback((newElements: WhiteboardElement[]) => {
    setHistory(prev => {
      const newHistory = prev.slice(0, historyIndex + 1);
      newHistory.push(JSON.parse(JSON.stringify(newElements)));
      if (newHistory.length > 50) newHistory.shift();
      return newHistory;
    });
    setHistoryIndex(prev => Math.min(prev + 1, 49));
  }, [historyIndex]);
  
  const undo = useCallback(() => {
    if (historyIndex > 0) {
      setHistoryIndex(prev => prev - 1);
      setElements(JSON.parse(JSON.stringify(history[historyIndex - 1])));
    }
  }, [historyIndex, history]);
  
  const redo = useCallback(() => {
    if (historyIndex < history.length - 1) {
      setHistoryIndex(prev => prev + 1);
      setElements(JSON.parse(JSON.stringify(history[historyIndex + 1])));
    }
  }, [historyIndex, history]);
  
  // ============================================================================
  // Element Management
  // ============================================================================
  const handleAddElement = useCallback((element: WhiteboardElement) => {
    const newElements = [...elements, { ...element, zIndex: elements.length + 1 }];
    setElements(newElements);
    pushHistory(newElements);
    setActiveTool('select');
  }, [elements, pushHistory]);
  
  const handleUpdateElement = useCallback((id: string, updates: Partial<WhiteboardElement>) => {
    const newElements = elements.map(el => 
      el.id === id ? { ...el, ...updates } : el
    );
    setElements(newElements);
  }, [elements]);
  
  const handleDeleteElement = useCallback((id: string) => {
    const newElements = elements.filter(el => el.id !== id);
    setElements(newElements);
    pushHistory(newElements);
    setSelectedIds(prev => prev.filter(i => i !== id));
  }, [elements, pushHistory]);
  
  
  // ============================================================================
  // Zoom Controls
  // ============================================================================
  const handleZoomIn = useCallback(() => {
    setZoom(z => Math.min(3, z + 0.25));
  }, []);
  
  const handleZoomOut = useCallback(() => {
    setZoom(z => Math.max(0.25, z - 0.25));
  }, []);
  
  const handleZoomReset = useCallback(() => {
    setZoom(1);
    setPan({ x: 100, y: 100 });
  }, []);
  
  const handleZoomFit = useCallback(() => {
    if (elements.length === 0) {
      handleZoomReset();
      return;
    }
    
    const minX = Math.min(...elements.map(el => el.x));
    const minY = Math.min(...elements.map(el => el.y));
    const maxX = Math.max(...elements.map(el => el.x + el.width));
    const maxY = Math.max(...elements.map(el => el.y + el.height));
    
    const contentWidth = maxX - minX + 100;
    const contentHeight = maxY - minY + 100;
    
    const viewportWidth = window.innerWidth - 400;
    const viewportHeight = window.innerHeight - 60;
    
    const scaleX = viewportWidth / contentWidth;
    const scaleY = viewportHeight / contentHeight;
    const newZoom = Math.min(scaleX, scaleY, 1);
    
    setZoom(newZoom);
    setPan({
      x: (viewportWidth - contentWidth * newZoom) / 2 - minX * newZoom + 50,
      y: (viewportHeight - contentHeight * newZoom) / 2 - minY * newZoom + 50,
    });
  }, [elements, handleZoomReset]);
  
  // ============================================================================
  // Keyboard Shortcuts
  // ============================================================================
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if ((e.target as HTMLElement).tagName === 'INPUT' || 
          (e.target as HTMLElement).tagName === 'TEXTAREA') {
        return;
      }
      
      if ((e.metaKey || e.ctrlKey) && e.key === 'z' && !e.shiftKey) {
        e.preventDefault();
        undo();
      }
      
      if ((e.metaKey || e.ctrlKey) && e.key === 'z' && e.shiftKey) {
        e.preventDefault();
        redo();
      }
      
      if (e.key === 'v') setActiveTool('select');
      if (e.key === 'h') setActiveTool('pan');
      if (e.key === 'b') setActiveTool('button');
      if (e.key === 'i') setActiveTool('input');
      if (e.key === 'd') setActiveTool('dropdown');
      if (e.key === 't') setActiveTool('table');
      if (e.key === 'x') setActiveTool('text');
      if (e.key === 'r') setActiveTool('shape_rect');
      if (e.key === 'o') setActiveTool('shape_circle');
      
      if ((e.metaKey || e.ctrlKey) && e.key === '=') {
        e.preventDefault();
        handleZoomIn();
      }
      if ((e.metaKey || e.ctrlKey) && e.key === '-') {
        e.preventDefault();
        handleZoomOut();
      }
      if ((e.metaKey || e.ctrlKey) && e.key === '0') {
        e.preventDefault();
        handleZoomReset();
      }
    };
    
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [undo, redo, handleZoomIn, handleZoomOut, handleZoomReset]);
  
  useEffect(() => {
    const handleWheel = (e: WheelEvent) => {
      if (e.ctrlKey || e.metaKey) {
        e.preventDefault();
        const delta = e.deltaY > 0 ? -0.1 : 0.1;
        setZoom(z => Math.min(3, Math.max(0.25, z + delta)));
      }
    };
    
    window.addEventListener('wheel', handleWheel, { passive: false });
    return () => window.removeEventListener('wheel', handleWheel);
  }, []);
  
  // ============================================================================
  // Fullscreen
  // ============================================================================
  const toggleFullscreen = useCallback(() => {
    if (!document.fullscreenElement) {
      document.documentElement.requestFullscreen();
      setIsFullscreen(true);
    } else {
      document.exitFullscreen();
      setIsFullscreen(false);
    }
  }, []);
  
  // ============================================================================
  // Export
  // ============================================================================
  const handleExport = useCallback(() => {
    const data = {
      userStory,
      elements,
      acceptanceCriteria,
      exportedAt: new Date().toISOString(),
    };
    
    const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `whiteboard-${Date.now()}.json`;
    a.click();
    URL.revokeObjectURL(url);
    
    setSaved(true);
    setTimeout(() => setSaved(false), 2000);
  }, [userStory, elements, acceptanceCriteria]);
  
  // ============================================================================
  // Render
  // ============================================================================
  return (
    <div className="h-screen flex flex-col bg-main overflow-hidden">
      {/* Header */}
      <header className="flex items-center justify-between px-4 py-2 bg-surface border-b border-default shrink-0">
        <div className="flex items-center gap-3">
          <Link 
            to="/" 
            className="btn-icon"
            title="Back to home"
          >
            <ArrowLeft className="w-5 h-5" />
          </Link>
          
          <div className="h-6 w-px bg-border" />
          
          <div>
            <h1 className="text-base font-semibold text-default flex items-center gap-2">
              Whiteboard
              <span className="px-2 py-0.5 text-[10px] font-medium bg-primary-dim text-primary rounded-full">
                Demo
              </span>
            </h1>
            <p className="text-xs text-muted">Create wireframes and user stories</p>
          </div>
        </div>
        
        <div className="flex items-center gap-2">
          {/* Undo/Redo */}
          <div className="flex items-center gap-0.5 mr-2">
            <button 
              onClick={undo}
              disabled={historyIndex === 0}
              className="btn-icon disabled:opacity-30"
              title="Undo (⌘Z)"
            >
              <Undo className="w-4 h-4" />
            </button>
            <button 
              onClick={redo}
              disabled={historyIndex >= history.length - 1}
              className="btn-icon disabled:opacity-30"
              title="Redo (⌘⇧Z)"
            >
              <Redo className="w-4 h-4" />
            </button>
          </div>
          
          <div className="h-6 w-px bg-border" />
          
          {/* Zoom controls */}
          <div className="flex items-center gap-1 px-2 py-1 bg-surface-2 rounded-lg">
            <button 
              onClick={handleZoomOut} 
              className="p-1 rounded hover:bg-surface-3 text-muted hover:text-default"
              title="Zoom out"
            >
              <ZoomOut className="w-4 h-4" />
            </button>
            <button 
              onClick={handleZoomReset}
              className="text-sm font-medium min-w-[4rem] text-center hover:bg-surface-3 rounded px-2 py-0.5"
              title="Reset zoom"
            >
              {Math.round(zoom * 100)}%
            </button>
            <button 
              onClick={handleZoomIn} 
              className="p-1 rounded hover:bg-surface-3 text-muted hover:text-default"
              title="Zoom in"
            >
              <ZoomIn className="w-4 h-4" />
            </button>
          </div>
          
          <button 
            onClick={handleZoomFit}
            className="btn-icon"
            title="Fit to content"
          >
            <Maximize2 className="w-4 h-4" />
          </button>
          
          <div className="h-6 w-px bg-border" />
          
          {/* Grid toggle */}
          <button 
            onClick={() => setShowGrid(!showGrid)} 
            className={`btn-icon ${showGrid ? 'bg-primary-dim text-primary' : ''}`}
            title="Toggle grid"
          >
            <Grid3X3 className="w-4 h-4" />
          </button>
          
          {/* Snap toggle */}
          <button 
            onClick={() => setSnapToGrid(!snapToGrid)} 
            className={`btn-icon ${snapToGrid ? 'bg-primary-dim text-primary' : ''}`}
            title="Snap to grid"
          >
            <Magnet className="w-4 h-4" />
          </button>
          
          {/* Fullscreen */}
          <button 
            onClick={toggleFullscreen}
            className="btn-icon"
            title="Toggle fullscreen"
          >
            {isFullscreen ? <Minimize2 className="w-4 h-4" /> : <Maximize2 className="w-4 h-4" />}
          </button>
          
          <div className="h-6 w-px bg-border" />
          
          {/* Export button */}
          <button 
            onClick={handleExport}
            className={`btn px-4 py-2 ${saved ? 'btn-success' : 'btn-primary'}`}
          >
            {saved ? (
              <>
                <Check className="w-4 h-4" />
                Exported!
              </>
            ) : (
              <>
                <Download className="w-4 h-4" />
                Export JSON
              </>
            )}
          </button>
        </div>
      </header>
      
      {/* Main content */}
      <div className="flex flex-1 overflow-hidden">
        {/* Left toolbar */}
        <WhiteboardToolbar 
          activeTool={activeTool}
          onToolChange={setActiveTool}
        />
        
        {/* Canvas */}
        <div className="flex-1 relative overflow-hidden">
          <WhiteboardCanvas
            elements={elements}
            frames={[]}
            arrows={[]}
            selectedIds={selectedIds}
            selectedFrameId={null}
            focusedFrameId={null}
            zoom={zoom}
            pan={pan}
            showGrid={showGrid}
            gridSize={gridSize}
            snapToGrid={snapToGrid}
            activeTool={activeTool}
            guides={[]}
            onSelect={setSelectedIds}
            onSelectFrame={() => {}}
            onUpdate={handleUpdateElement}
            onDelete={handleDeleteElement}
            onAddElement={handleAddElement}
            onPanChange={setPan}
            onAddFrame={() => {}}
            onUpdateFrame={() => {}}
            onDeleteFrame={() => {}}
            onDuplicateFrame={() => {}}
            onChangeDevice={() => {}}
            onAddArrow={() => {}}
            onSelectArrow={() => {}}
            selectedArrowId={null}
            findFrameAtPoint={() => null}
            onElementDuplicate={() => {}}
          />
          
          {/* Mini info bar */}
          <div className="absolute bottom-4 left-4 flex items-center gap-3 px-3 py-2 bg-surface/90 backdrop-blur-sm rounded-lg border border-default shadow-sm text-xs text-muted">
            <span>{elements.length} elements</span>
            <span className="w-px h-4 bg-border" />
            <span>{selectedIds.length} selected</span>
            <span className="w-px h-4 bg-border" />
            <span>Grid: {gridSize}px</span>
          </div>
          
          {/* Keyboard shortcuts hint */}
          <div className="absolute bottom-4 right-4 px-3 py-2 bg-surface/90 backdrop-blur-sm rounded-lg border border-default shadow-sm text-xs text-muted">
            <span className="font-medium">Shortcuts:</span> V=Select, H=Pan, B=Button, T=Table, X=Text
          </div>
        </div>
        
        {/* Right panel - User Story */}
        <WhiteboardUserStory
          projectId="demo"
          userStory={userStory}
          elements={elements}
          acceptanceCriteria={acceptanceCriteria}
          onUserStoryChange={setUserStory}
          onAcceptanceCriteriaChange={setAcceptanceCriteria}
        />
      </div>
    </div>
  );
}

